// SPDX-FileCopyrightText: 2026 Stadtplatz.de
//
// SPDX-License-Identifier: EUPL-1.2

import { useMemo, useState } from "react";

// Stable default so an omitted `accessors` arg doesn't bust the memo each render.
const EMPTY_ACCESSORS = {};

/**
 * Generic client-side sorting for list tables.
 *
 * Pairs with the <SortableHeader> component: pass `sortConfig` + `requestSort`
 * to each sortable header, and render `sortedItems` instead of the raw array.
 *
 * Sorting rules:
 *  - numbers compare numerically; everything else via locale-aware string
 *    compare with `numeric: true` (so codes like "l7" < "l18" sort naturally);
 *  - empty values (null / undefined / "") always sink to the bottom regardless
 *    of direction;
 *  - clicking a header cycles asc → desc → unsorted.
 *
 * NOTE: this sorts the array it's given. For server-paginated tables that hold
 * only the current page, it sorts that page — not the full dataset.
 *
 * @param {Array} items
 * @param {Object<string, (row:any)=>any>} accessors  per-key value getters for
 *        columns whose sort value isn't simply `row[key]` (must be stable —
 *        define at module scope, not inline).
 * @param {{key:string, direction:"asc"|"desc"}|null} initial  initial sort.
 */
export const useSortableData = (
  items,
  accessors = EMPTY_ACCESSORS,
  initial = null,
) => {
  const [sortConfig, setSortConfig] = useState(initial);

  const sortedItems = useMemo(() => {
    const list = Array.isArray(items) ? items : [];
    if (!sortConfig?.key) return list;

    const getValue =
      accessors[sortConfig.key] || ((row) => row?.[sortConfig.key]);
    const dir = sortConfig.direction === "desc" ? -1 : 1;

    return [...list].sort((a, b) => {
      const av = getValue(a);
      const bv = getValue(b);

      const aEmpty = av == null || av === "";
      const bEmpty = bv == null || bv === "";
      if (aEmpty && bEmpty) return 0;
      if (aEmpty) return 1; // empties sink regardless of direction
      if (bEmpty) return -1;

      if (typeof av === "number" && typeof bv === "number") {
        return (av - bv) * dir;
      }
      return (
        String(av).localeCompare(String(bv), undefined, {
          numeric: true,
          sensitivity: "base",
        }) * dir
      );
    });
  }, [items, sortConfig, accessors]);

  // asc → desc → unsorted
  const requestSort = (key) =>
    setSortConfig((prev) => {
      if (prev?.key !== key) return { key, direction: "asc" };
      if (prev.direction === "asc") return { key, direction: "desc" };
      return null;
    });

  return { sortedItems, sortConfig, requestSort };
};

export default useSortableData;
